import {Location} from '@angular/common'
import {Injectable, inject} from '@angular/core'
import {Router} from '@angular/router'
import {Actions, createEffect, ofType} from '@ngrx/effects'
import {map, switchMap} from 'rxjs/operators'
import {ActivityRowItem} from '@models/activity'
import {Tables} from '@models/db.models'
import {UserInfo} from '@models/user-permissions'
import {UVEuser} from '@models/uve-user'
import {ActivityService} from '@service/activity.service'
import {ApiService} from '@service/api.service'
import {CoreActionTypes, getPermissionsResponse, navigateToResponse, setActivities} from './core.actions'
import {ActivitiesListMode} from './core.reducer'

/**
 * Efectos NgRx asociados al slice `core`.
 *
 * @description
 * Concentran la lógica asíncrona (navegación programática, resolución de
 * permisos y carga de actividades) que corresponde a las acciones del core.
 * Se mantienen aislados del reducer para que este siga siendo puro.
 *
 * @usageNotes
 *
 * ### Uso básico
 *
 * ```typescript
 * EffectsModule.forRoot([CoreEffects])
 * ```
 *
 * ### Best Practices
 * - Inyecta `CoreEffects` una única vez en el módulo raíz; el resto de
 *   features registran sus propios efectos con `forFeature`.
 * - No hagas navegación directa desde servicios: despacha `navigateTo` y deja
 *   que `navigateTo$` resuelva en un solo lugar.
 */
@Injectable()
export class CoreEffects {
  private readonly actions$ = inject(Actions)
  private readonly router = inject(Router)
  private readonly location = inject(Location)
  private readonly apiService = inject(ApiService)
  private readonly activityService = inject(ActivityService)

  /**
   * Resuelve la navegación solicitada con `navigateTo`.
   *
   * @description
   * Si `args` trae elementos, invoca `Router.navigate`; si viene vacío, usa
   * `Location.back()` para volver al paso anterior. En ambos casos emite
   * `navigateToResponse` para que los efectos encadenados sepan que la
   * navegación terminó.
   */
  navigateTo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionTypes.NAVIGATE_URL),

      map(({args}: {args: string[]}) => {
        if (args.length) {
          this.router.navigate([...args])
        } else {
          this.location.back()
        }
        return navigateToResponse()
      })
    )
  )

  /**
   * Carga los permisos del usuario autenticado desde la tabla `InfoUsers`.
   *
   * @description
   * Escucha `getPermissionsRequest`, consulta el documento del usuario por su
   * `userName` vía `ApiService.getItem` y emite `getPermissionsResponse` con
   * el array de permisos resuelto (o `undefined` si el documento no trae el
   * campo). El reducer los guarda en `state.permissions`.
   */
  permissionsRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionTypes.GET_PERMISSIONS_REQUEST),
      switchMap(({user}: {user: UVEuser}) => this.apiService.getItem(Tables.InfoUsers, user.userName)),
      map((info: UserInfo) => getPermissionsResponse({permissions: info?.permissions}))
    )
  )

  /**
   * Carga la lista de actividades filtradas por usuario.
   *
   * @description
   * Tras `getUserActivitiesList`, delega en `ActivityService.getUserActivities`
   * y aplica `mapActivities` para homogeneizar la forma. Despacha
   * `setActivities` con `mode = USER` para que los componentes sepan bajo qué
   * criterio se cargó el listado.
   */
  userActivitiesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionTypes.GET_USER_ACTIVITIES_LIST),
      switchMap(({id}) => this.activityService.getUserActivities(id)),
      map((activities: ActivityRowItem[]) => {
        activities = this.activityService.mapActivities(activities)
        return setActivities({
          list: activities,
          mode: ActivitiesListMode.USER,
        })
      })
    )
  )

  /**
   * Carga la lista de actividades filtradas por outlet (PDV).
   *
   * @description
   * Análogo a `userActivitiesList$` pero en modo `OUTLET`. La ficha del outlet
   * despacha `getOutletActivitiesList` al abrirse para mostrar el histórico
   * de actividades del punto de venta.
   */
  outletActivitiesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionTypes.GET_OUTLET_ACTIVITIES_LIST),
      switchMap(({id}) => this.activityService.getOutletActivities(id)),
      map((activities: ActivityRowItem[]) => {
        activities = this.activityService.mapActivities(activities)
        return setActivities({
          list: activities,
          mode: ActivitiesListMode.OUTLET,
        })
      })
    )
  )
}
